import React, { useState, useCallback } from 'react';
import {
  PlusIcon,
  TrashIcon,
  InformationCircleIcon
} from '@heroicons/react/24/outline';

const FieldPropertiesPanel = ({ field, allFields, onUpdateField, readOnly = false }) => {
  const [validationErrors, setValidationErrors] = useState({});

  const handleUpdate = useCallback((updates) => {
    if (!readOnly && field && onUpdateField) {
      onUpdateField(field.id, updates);
      setValidationErrors({});
    }
  }, [field, onUpdateField, readOnly]);

  const handleValidationUpdate = useCallback((validation) => {
    handleUpdate({ validation: { ...field?.validation, ...validation } });
  }, [field?.validation, handleUpdate]);

  const handleSettingsUpdate = useCallback((settings) => {
    handleUpdate({ settings: { ...field?.settings, ...settings } });
  }, [field?.settings, handleUpdate]);

  const addOption = useCallback(() => {
    const currentOptions = field?.options || [];
    const newOption = {
      value: `option_${currentOptions.length + 1}`,
      label: `Option ${currentOptions.length + 1}`
    };
    handleUpdate({ options: [...currentOptions, newOption] });
  }, [field?.options, handleUpdate]);

  const updateOption = useCallback((index, optionUpdates) => {
    const currentOptions = field?.options || [];
    const updatedOptions = currentOptions.map((option, i) => 
      i === index ? { ...option, ...optionUpdates } : option
    );
    handleUpdate({ options: updatedOptions });
  }, [field?.options, handleUpdate]);

  const removeOption = useCallback((index) => {
    const currentOptions = field?.options || [];
    const updatedOptions = currentOptions.filter((_, i) => i !== index);
    handleUpdate({ options: updatedOptions });
  }, [field?.options, handleUpdate]);

  if (!field) {
    return (
      <div className="text-center py-12">
        <InformationCircleIcon className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-white">
          No field selected
        </h3>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Select a field from the form canvas to configure its properties.
        </p>
      </div>
    );
  }

  const hasOptions = ['select', 'radio', 'checkbox'].includes(field.type);

  return (
    <div className="space-y-6">
      {/* Basic Properties */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Basic Properties
        </h4>
        
        <div className="space-y-4">
          {/* Field ID (read-only) */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Field ID
            </label>
            <input
              type="text"
              value={field.id}
              readOnly
              className="w-full px-3 py-2 text-sm bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md text-gray-500 dark:text-gray-400"
            />
          </div>

          {/* Label */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Label *
            </label>
            <input
              type="text"
              value={field.label || ''}
              onChange={(e) => handleUpdate({ label: e.target.value })}
              readOnly={readOnly}
              className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Enter field label"
            />
          </div>

          {/* Placeholder */}
          {['text', 'textarea', 'email', 'phone', 'number'].includes(field.type) && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Placeholder
              </label>
              <input
                type="text"
                value={field.placeholder || ''}
                onChange={(e) => handleUpdate({ placeholder: e.target.value })}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter placeholder text"
              />
            </div>
          )}

          {/* Default Value */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Default Value
            </label>
            {hasOptions ? (
              <select
                value={field.default_value || ''}
                onChange={(e) => handleUpdate({ default_value: e.target.value })}
                disabled={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              >
                <option value="">No default</option>
                {field.options?.map((option, index) => (
                  <option key={index} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type={field.type === 'number' ? 'number' : 'text'}
                value={field.default_value || ''}
                onChange={(e) => handleUpdate({ default_value: e.target.value })}
                readOnly={readOnly}
                className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                placeholder="Enter default value"
              />
            )}
          </div>

          {/* Required */}
          <div className="flex items-center">
            <input
              type="checkbox"
              id={`required-${field.id}`}
              checked={field.required || false}
              onChange={(e) => handleUpdate({ required: e.target.checked })}
              disabled={readOnly}
              className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
            />
            <label htmlFor={`required-${field.id}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              Required field
            </label>
          </div>
        </div>
      </div>

      {/* Options (for select, radio, checkbox) */}
      {hasOptions && (
        <div>
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white">
              Options
            </h4>
            {!readOnly && (
              <button
                onClick={addOption}
                className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:text-purple-500"
              >
                <PlusIcon className="h-3 w-3 mr-1" />
                Add Option
              </button>
            )}
          </div>

          <div className="space-y-2">
            {field.options?.map((option, index) => (
              <div key={index} className="flex items-center space-x-2">
                <input
                  type="text"
                  value={option.value}
                  onChange={(e) => updateOption(index, { value: e.target.value })}
                  readOnly={readOnly}
                  placeholder="Value"
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
                <input
                  type="text"
                  value={option.label}
                  onChange={(e) => updateOption(index, { label: e.target.value })}
                  readOnly={readOnly}
                  placeholder="Label"
                  className="flex-1 px-2 py-1 text-xs border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
                {!readOnly && (
                  <button
                    onClick={() => removeOption(index)}
                    className="p-1 text-red-500 hover:text-red-700"
                  >
                    <TrashIcon className="h-3 w-3" />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Validation Rules */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Validation
        </h4>
        
        <div className="space-y-4">
          {/* Text length validation */}
          {['text', 'textarea'].includes(field.type) && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Min Length
                  </label>
                  <input
                    type="number"
                    value={field.validation?.minLength || ''}
                    onChange={(e) => handleValidationUpdate({ minLength: parseInt(e.target.value) || undefined })}
                    readOnly={readOnly}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                    min="0"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Length
                  </label>
                  <input
                    type="number"
                    value={field.validation?.maxLength || ''}
                    onChange={(e) => handleValidationUpdate({ maxLength: parseInt(e.target.value) || undefined })}
                    readOnly={readOnly}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                    min="1"
                  />
                </div>
              </div>
            </>
          )}

          {/* Number validation */}
          {field.type === 'number' && (
            <>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Min Value
                  </label>
                  <input
                    type="number"
                    value={field.validation?.min || ''}
                    onChange={(e) => handleValidationUpdate({ min: parseFloat(e.target.value) || undefined })}
                    readOnly={readOnly}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Max Value
                  </label>
                  <input
                    type="number"
                    value={field.validation?.max || ''}
                    onChange={(e) => handleValidationUpdate({ max: parseFloat(e.target.value) || undefined })}
                    readOnly={readOnly}
                    className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Step
                </label>
                <input
                  type="number"
                  value={field.validation?.step || ''}
                  onChange={(e) => handleValidationUpdate({ step: parseFloat(e.target.value) || undefined })}
                  readOnly={readOnly}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  min="0.01"
                  step="0.01"
                />
              </div>
            </>
          )}

          {/* File validation */}
          {field.type === 'file' && (
            <>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Accepted File Types
                </label>
                <input
                  type="text"
                  value={field.validation?.accept || ''}
                  onChange={(e) => handleValidationUpdate({ accept: e.target.value })}
                  readOnly={readOnly}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder=".pdf,.doc,.jpg,.png"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max File Size
                </label>
                <input
                  type="text"
                  value={field.validation?.maxSize || ''}
                  onChange={(e) => handleValidationUpdate({ maxSize: e.target.value })}
                  readOnly={readOnly}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                  placeholder="10MB"
                />
              </div>
            </>
          )}

          {/* Date validation */}
          {field.type === 'date' && (
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Min Date
                </label>
                <input
                  type="date"
                  value={field.validation?.min || ''}
                  onChange={(e) => handleValidationUpdate({ min: e.target.value })}
                  readOnly={readOnly}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Max Date
                </label>
                <input
                  type="date"
                  value={field.validation?.max || ''}
                  onChange={(e) => handleValidationUpdate({ max: e.target.value })}
                  readOnly={readOnly}
                  className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                />
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Field Settings */}
      <div>
        <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
          Display Settings
        </h4>
        
        <div className="space-y-4">
          {/* Help Text */}
          <div>
            <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
              Help Text
            </label>
            <textarea
              value={field.settings?.helpText || ''}
              onChange={(e) => handleSettingsUpdate({ helpText: e.target.value })}
              readOnly={readOnly}
              rows={2}
              className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
              placeholder="Additional help text for users"
            />
          </div>

          {/* Textarea rows */}
          {field.type === 'textarea' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Rows
              </label>
              <input
                type="number"
                value={field.settings?.rows || 4}
                onChange={(e) => handleSettingsUpdate({ rows: parseInt(e.target.value) || 4 })}
                readOnly={readOnly}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                min="1"
                max="20"
              />
            </div>
          )}

          {/* File multiple */}
          {field.type === 'file' && (
            <div className="flex items-center">
              <input
                type="checkbox"
                id={`multiple-${field.id}`}
                checked={field.settings?.multiple || false}
                onChange={(e) => handleSettingsUpdate({ multiple: e.target.checked })}
                disabled={readOnly}
                className="h-4 w-4 text-purple-600 focus:ring-purple-500 border-gray-300 dark:border-gray-600 rounded"
              />
              <label htmlFor={`multiple-${field.id}`} className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                Allow multiple files
              </label>
            </div>
          )}

          {/* Rating max */}
          {field.type === 'rating' && (
            <div>
              <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">
                Max Rating
              </label>
              <input
                type="number"
                value={field.settings?.max || 5}
                onChange={(e) => handleSettingsUpdate({ max: parseInt(e.target.value) || 5 })}
                readOnly={readOnly}
                className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:ring-purple-500 focus:border-purple-500 dark:bg-gray-700 dark:text-white"
                min="3"
                max="10"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FieldPropertiesPanel;